import { Component, inject } from '@angular/core';
import { SnackbarService, FeedbackInfoComponent } from 'ok-angular-shared/components';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ChargingStationLocationService } from 'src/app/shared/services/charging-station-location.service';
import { ChargingStationLocationDetails, DYNAMIC_PRICE_POLICY_NET_AREA_MESSAGE } from '../charging-station-location-model';
import { MatDialogTitle, MatDialogContent, MatDialogActions, MatDialogClose } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { ReactiveFormsModule } from '@angular/forms';
import { AdmPricePolicyTypeaheadComponent } from 'src/app/shared/components/adm-price-policy-typeahead/adm-price-policy-typeahead.component';
import { PricePolicyType, PricePolicy } from '../../prices/price-policies/interfaces/price-policy';
import { ConnectorService } from 'src/app/shared/services/connector.service';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, finalize, forkJoin, map, of } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { FeatureFlagService } from 'src/app/shared/services/feature-flag.service';
import { FEATURE_FLAGS } from 'src/app/shared/services/feature-flags.const';

interface MultipleConnectorPricesPolicyDialogForm {
  pricePolicyId: FormControl<number | null>;
}

export interface MultipleConnectorPricesPolicyDialogData {
  chargingStationLocationIdentifier: string;
  canHaveDynamicPricePolicy: boolean;
}

@Component({
  selector: 'adm-multiple-connector-prices-policy-dialog',
  templateUrl: './multiple-connector-prices-policy-dialog.component.html',
  styleUrls: ['./multiple-connector-prices-policy-dialog.component.scss'],
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatIcon,
    MatButton,
    ReactiveFormsModule,
    FeedbackInfoComponent,
    AdmPricePolicyTypeaheadComponent,
  ],
})
export class MultipleConnectorPricesPolicyDialogComponent {
  dialogRef = inject<MatDialogRef<MultipleConnectorPricesPolicyDialogComponent>>(MatDialogRef);
  private snackbarService = inject(SnackbarService);
  private chargingStationLocationService = inject(ChargingStationLocationService);
  private connectorService = inject(ConnectorService);
  data = inject<MultipleConnectorPricesPolicyDialogData>(MAT_DIALOG_DATA);

  private readonly validateNetAreaForPricePolicy = toSignal(
    inject(FeatureFlagService)
      .getFeatureFlag(FEATURE_FLAGS.VALIDATE_NET_AREA_FOR_PRICE_POLICY)
      .pipe(map(flag => flag.result)),
    { initialValue: false }
  );

  public readonly dynamicPricePolicyWarning = DYNAMIC_PRICE_POLICY_NET_AREA_MESSAGE;
  public selectedPricePolicy: PricePolicy | null = null;
  public loading = false;
  public formGroup: FormGroup<MultipleConnectorPricesPolicyDialogForm> = new FormGroup<MultipleConnectorPricesPolicyDialogForm>({
    pricePolicyId: new FormControl<number | null>(null, Validators.required),
  });
  public initialPricePolicyId: number | null = null;
  public chargingStationLocationDetails: ChargingStationLocationDetails | null = null;
  public connectorCount: number = 0;
  public organizationId: number | null = null;

  constructor() {
    this.loading = true;

    forkJoin({
      details: this.chargingStationLocationService.getChargingStationLocationDetails(this.data.chargingStationLocationIdentifier),
      connectorsResponse: this.connectorService.getConnectors({
        locationIdentifier: this.data.chargingStationLocationIdentifier,
        pageNumber: 0,
        pageSize: 1000,
      }),
    })
      .pipe(
        catchError(() => {
          this.snackbarService.error('Der skete en fejl under indlæsning af data til prispolitik-dialogen.');
          this.loading = false;
          return of(null);
        }),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe(response => {
        if (!response) {
          return;
        }

        this.chargingStationLocationDetails = response.details;
        this.organizationId = response.details.organizationId;
        this.connectorCount = response.connectorsResponse.connectors.length;

        const defaultPricePolicyId = response.details.defaultPricePolicy?.id ?? null;
        this.initialPricePolicyId = defaultPricePolicyId;
        this.pricePolicyId.setValue(defaultPricePolicyId);
      });
  }

  get canHaveDynamicPricePolicy(): boolean {
    return !this.validateNetAreaForPricePolicy() || this.data.canHaveDynamicPricePolicy === true;
  }

  get pricePolicyId(): FormControl<number | null> {
    return this.formGroup.controls.pricePolicyId;
  }

  get pricePolicyType(): PricePolicyType {
    return this.chargingStationLocationDetails?.hasOkPowerMeter ? PricePolicyType.FixedWithOkMeter : PricePolicyType.FixedWithoutOkMeter;
  }

  onPricePolicySelected(pricePolicy: PricePolicy | null): void {
    this.selectedPricePolicy = pricePolicy;
    this.pricePolicyId.setValue(pricePolicy?.id ?? null);
  }

  public close() {
    this.dialogRef.close();
  }

  savePricePolicy(): void {
    if (!this.canHaveDynamicPricePolicy && this.selectedPricePolicy?.type === PricePolicyType.Dynamic) {
      this.snackbarService.warning(this.dynamicPricePolicyWarning);
      return;
    }

    const selectedPricePolicyId = this.pricePolicyId.value;
    if (selectedPricePolicyId == null || !this.chargingStationLocationDetails) {
      return;
    }

    this.loading = true;
    this.chargingStationLocationService
      .setPricePolicy(selectedPricePolicyId.toString(), this.chargingStationLocationDetails.chargingStationLocationIdentifier)
      .subscribe({
        next: () => {
          this.dialogRef.close(this.chargingStationLocationDetails);
        },
        error: (httpError: HttpErrorResponse) => {
          if (httpError.error?.code === 100613) {
            this.snackbarService.error(
              'Den valgte prispolitik passer ikke til den valgte OK-måler indstilling. Vælg en anden prispolitik og prøv igen.'
            );
          } else if (httpError.error?.code === 100620) {
            this.snackbarService.error(this.dynamicPricePolicyWarning);
          } else {
            this.snackbarService.error('Der skete en fejl under opdatering af prispolitik.');
          }
        },
      })
      .add(() => {
        this.loading = false;
      });
  }
}
